import React from 'react';
import type { Database } from '../../lib/database.types';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import AddressAutocomplete from '../../components/AddressAutocomplete';

interface TechnicianModalProps {
  technician?: Database['public']['Tables']['technicians']['Row'];
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}

const TechnicianModal: React.FC<TechnicianModalProps> = ({ technician, onClose, onSubmit }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [addressData, setAddressData] = React.useState<{
    address: string;
    city: string;
    state: string;
    zip_code: string;
  } | null>(null);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{technician ? 'Edit Technician' : 'Add New Technician'}</DialogTitle>
          <DialogDescription>
            {technician ? 'Update technician details' : 'Add a new service technician to your team'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
            {error}
          </div>
        )}

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setLoading(true);
            setError(null);

            try {
              const form = e.target as HTMLFormElement;
              const formData = new FormData(form);
              const technicianData = {
                full_name: formData.get('full_name') as string,
                phone: formData.get('phone') as string,
                email: formData.get('email') as string || null,
                address: addressData?.address || technician?.address || null,
                city: addressData?.city || technician?.city || null,
                state: addressData?.state || technician?.state || null,
                zip_code: addressData?.zip_code || technician?.zip_code || null,
                emergency_contact: formData.get('emergency_contact') as string || null,
                emergency_phone: formData.get('emergency_phone') as string || null,
                notes: formData.get('notes') as string || null,
              };

              await onSubmit(technicianData);
              onClose();
            } catch (err) {
              console.error('Failed to save technician:', err);
              setError(err instanceof Error ? err.message : 'Failed to save technician');
            } finally {
              setLoading(false);
            }
          }}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                name="full_name"
                required
                defaultValue={technician?.full_name}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                required
                defaultValue={technician?.phone}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              defaultValue={technician?.email || ''}
            />
          </div>

          <div className="space-y-2">
            <Label>Address</Label>
            <AddressAutocomplete
              onAddressSelect={setAddressData}
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="emergency_contact">Emergency Contact</Label>
              <Input
                id="emergency_contact"
                name="emergency_contact"
                defaultValue={technician?.emergency_contact || ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emergency_phone">Emergency Phone</Label>
              <Input
                id="emergency_phone"
                name="emergency_phone"
                type="tel"
                defaultValue={technician?.emergency_phone || ''}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              name="notes"
              rows={3}
              defaultValue={technician?.notes || ''}
              placeholder="Add any relevant notes about the technician..."
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : technician ? 'Save Changes' : 'Add Technician'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TechnicianModal;